import Form, { FormProps } from "@/shared/form/form";
import Input from "@/shared/form/input";
import { LocalizedValue } from "@/shared/form/localized-value";
import { Fields } from "../fields";
import Checkbox from "@/shared/form/checkbox";
import { SubmitButton, SubmitWrapper } from "@/shared/form/button";
import { MetaClassSelector } from "../meta-class-selector";
import { useTranslations } from "use-intl";
import { UploadWithCrop } from "@/shared/form/upload";
import { onlyOneDocument } from "@/shared/form/upload/ui/attachment";
import { FieldsType } from "../../../model/types";

const defaultInitialValues: Partial<FieldsType> = {
  allowAttachments: false,
  autoPatchPersonData: false,
  active: true,
};

export function DynamicProcessInfoForm({
  partOf,
  initialValues,
  ...formProps
}: FormProps<FieldsType> & {
  partOf?: boolean;
}) {
  const isEditMode = !!initialValues;
  const [form] = Form.useForm<FieldsType>();
  const metaClass = Form.useWatch("metaClass", form);

  const t = useTranslations("DynamicProcessInfoForm");

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initialValues ?? defaultInitialValues}
      {...formProps}
    >
      <Form.Item<FieldsType> name="id" label="Id" hidden>
        <Input />
      </Form.Item>

      <Form.Item<FieldsType>
        name="icon"
        label={t("Icon")}
        {...onlyOneDocument()}
      >
        <UploadWithCrop maxCount={1} showUploadList />
      </Form.Item>

      <LocalizedValue
        name="name"
        label={t("Name")}
        render={({ placeholder }) => <Input placeholder={placeholder} />}
        myLocaleRules={[{ required: true }]}
      />

      <Form.Item<FieldsType>
        name="metaClass"
        label={t("Meta class")}
        rules={[{ required: true }]}
      >
        <MetaClassSelector isEditMode={isEditMode} />
      </Form.Item>

      {metaClass && (
        <Form.Item<FieldsType>
          name="fields"
          label={t("Fields")}
          rules={[{ required: true }]}
        >
          <Fields isEditMode={isEditMode} />
        </Form.Item>
      )}

      <Form.Item<FieldsType>
        name="allowAttachments"
        label={t("Allow attachments")}
        valuePropName="checked"
      >
        <Checkbox />
      </Form.Item>

      <Form.Item<FieldsType>
        name="autoPatchPersonData"
        label={t("Auto patch person data")}
        valuePropName="checked"
      >
        <Checkbox />
      </Form.Item>

      <Form.Item<FieldsType>
        name="active"
        label={t("Active")}
        valuePropName="checked"
      >
        <Checkbox />
      </Form.Item>

      {!partOf && (
        <SubmitWrapper>
          <SubmitButton />
        </SubmitWrapper>
      )}
    </Form>
  );
}
